// Labels and badge styling shared by the Tasks, Pool and Schedule tabs.
//
// These started out inside TasksList.js. The Daily Task Engine screens need
// exactly the same vocabulary — a task type has to read the same way on the
// schedule board as it does in the catalogue — so they live here rather than
// being copied into three files that could drift apart.

export const TASK_TYPE_LABEL = {
  website_visit: "Website Visit",
  download_app: "Download App",
  telegram: "Telegram",
  youtube: "YouTube",
};

export const TASK_TYPE_BADGE = "bg-blue-50 text-blue-700 border-blue-100";

export const PLATFORM_LABEL = {
  both: "Both",
  android: "Android",
  ios: "iOS",
};

export const PLATFORM_BADGE = {
  both: "bg-gray-50 text-gray-600 border-gray-200",
  android: "bg-emerald-50 text-emerald-700 border-emerald-100",
  ios: "bg-violet-50 text-violet-700 border-violet-100",
};

/**
 * Pool state — what the scheduler reads.
 *
 * Only `ready` is ever published, which is the one thing an admin has to be
 * able to see at a glance; the colours are chosen so `ready` stands out from
 * the three states that mean "will not run".
 */
export const POOL_STATUS_LABEL = {
  draft: "Draft",
  ready: "Ready",
  paused: "Paused",
  archived: "Archived",
};

export const POOL_STATUS_BADGE = {
  draft: "bg-amber-50 text-amber-700 border-amber-200",
  ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-gray-100 text-gray-500 border-gray-200",
  archived: "bg-gray-100 text-gray-400 border-gray-200",
};

/** Occurrence state — one day's publication of a task. */
export const OCCURRENCE_STATUS_LABEL = {
  scheduled: "Scheduled",
  live: "Live",
  expired: "Expired",
  pulled: "Pulled",
};

export const OCCURRENCE_STATUS_BADGE = {
  scheduled: "bg-blue-50 text-blue-700 border-blue-200",
  live: "bg-emerald-50 text-emerald-700 border-emerald-200",
  expired: "bg-gray-100 text-gray-400 border-gray-200",
  pulled: "bg-rose-50 text-rose-700 border-rose-200",
};

/** Why the generator built nothing for a day, in words an admin can act on. */
export const SKIP_REASON_LABEL = {
  already_generated: "Already full",
  no_running_event: "No running event",
  empty_pool: "Nothing eligible",
  nothing_selected: "Blocked by caps",
};

export const formatWhen = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d.getTime())
    ? "—"
    : d.toLocaleString("en-US", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/** "45s", "2m 05s", "1h 03m" — how long a task took, opened to completed. Same format as game history. */
export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) return "—";
  const s = Math.max(0, Math.round(Number(seconds)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, "0")}m`;
};

/** `YYYY-MM-DD` as `Mon 11 Sep`. Parsed as UTC, because the day itself is UTC. */
export const formatDay = (day) => {
  if (!day) return "—";
  const d = new Date(`${day}T00:00:00.000Z`);
  if (isNaN(d.getTime())) return day;
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
  });
};

/** A `DATE` value as `YYYY-MM-DD`, `offsetDays` from today, in UTC. */
export const utcDay = (offsetDays = 0) => {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
};

/**
 * The task type as coloured text rather than a filled pill.
 *
 * A row already carries a status, an event and two numbers. Giving the type a
 * pill as well meant four competing shapes per row and nothing standing out;
 * as text it still scans by colour without shouting.
 */
export const TASK_TYPE_TEXT = "text-blue-600";
